import { useEffect } from 'react';

import { PaginatedList } from '../../common/PaginatedList.interface';
import {
  Agreement,
  AgreementStatusLog,
  AgreementVersion
} from './Agreement.interface';
import { useApiPost, useApiPatch, useApiGet } from '../../common/api/Api.hooks';

export function useCreateAgreement() {
  const { data, isLoading, error, post } = useApiPost<Agreement>('agreements');
  return { agreement: data, isLoading, error, post };
}

export function useUpdateAgreement(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<Agreement>(
    `agreements/${id}`
  );
  return { agreement: data, isLoading, error, patch };
}

export function useRevertAgreement(id: number) {
  const { data, isLoading, error, post } = useApiPost<Agreement>(
    `agreements/${id}/revert`
  );
  return { agreement: data, isLoading, error, revert: post };
}

export function useFindAgreements() {
  const { data, isLoading, error, get } = useApiGet<PaginatedList<Agreement>>(
    'agreements'
  );
  return { list: data, isLoading, error, get };
}

export function useFindPublishedAgreementVersions(shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<AgreementVersion[]>(
    'agreements/published-versions'
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    publishedAgreementVersions: data,
    isLoading,
    error,
    findPublishedAgreementVersions: get
  };
}

export function useFindAgreement(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<Agreement>(
    `agreements/${id}`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { agreement: data, isLoading, error, get };
}
export function useGetAgreementStatusLogs(
  id: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<AgreementStatusLog>
  >(`agreements/${id}/status-log`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { agreementStatusLogs: data, isLoading, error, get };
}

export function useFindAgreementVersions(
  agreementId: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<AgreementVersion[]>(
    `agreements/${agreementId}/versions`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    agreementVersions: data,
    isLoading,
    error,
    findAgreementVersions: get
  };
}

export function useFindAgreementVersion(
  agreementId?: number,
  agreementVersionId?: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<AgreementVersion>(
    `agreements/${agreementId}/versions/${agreementVersionId}`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    agreementVersion: data,
    isLoading,
    error,
    findAgreementVersion: get
  };
}
